import { useState } from "react";
import { useOutletContext } from "react-router-dom";
import { adminApi } from "../api/admin";
import type {
  BulkUpdateRequest,
  Collection,
  Member,
  Video,
  VideoProvider,
  VideoRequest,
  Visibility,
} from "../api/types";
import { useAsync } from "../hooks/useAsync";
import { useCollections } from "../hooks/useCollections";
import { accession, formatDate, formatDuration, PROVIDER_LABELS } from "../lib/format";
import { AdminTable, type Column } from "./AdminTable";
import { AdminForm, type FieldDef, type FormValues } from "./AdminForm";
import type { AdminOutletContext } from "./AdminLayout";
import {
  isoToLocalInput,
  localInputToIso,
  parseTags,
  tagsToInput,
  titleFromFilename,
  toNullableNumber,
  toNullableString,
  toStringList,
} from "./formUtils";
import {
  accessFields,
  BulkDialog,
  mediaBulkActions,
  mediaFilters,
  Thumb,
  useMembers,
  VisibilityCell,
  type OpenBulkDialog,
} from "./mediaAdmin";
import { MediaImport } from "./MediaImport";
import { uploadVideoFile } from "./mediaTools";

const PROVIDER_OPTIONS = (Object.keys(PROVIDER_LABELS) as VideoProvider[]).map((p) => ({
  value: p,
  label: PROVIDER_LABELS[p],
}));

/** "Fetch details": ask the platform about the pasted link, and fill in whatever is still blank. */
async function fetchDetails(values: FormValues) {
  const info = await adminApi.lookupVideo(String(values.url ?? "").trim());
  return {
    fill: { provider: info.provider },
    fillIfEmpty: {
      title: info.title,
      description: info.description,
      thumbnailUrl: info.thumbnailUrl,
      durationSeconds: info.durationSeconds,
      publishedAt: isoToLocalInput(info.publishedAt),
    },
  };
}

/** Field list; the collection and member options come from the API, so new ones show up here too. */
function fields(collections: Collection[], members: Member[]): FieldDef[] {
  return [
    { name: "title", label: "Title", type: "text", required: true },
    { name: "description", label: "Description", type: "textarea" },
    {
      name: "collection",
      label: "Collection",
      type: "select",
      options: collections.map((c) => ({ value: c.id, label: c.label })),
      help: "Which shelf this sits on. A sectioned collection stays out of the main gallery.",
    },
    {
      name: "url",
      label: "Video",
      type: "upload",
      required: true,
      accept: "video/*",
      help: "Paste a link from YouTube, Vimeo, Dailymotion, Facebook, TikTok, Loom, Streamable — or any other site — or upload a video file.",
      action: { label: "Fetch details from the link", run: fetchDetails },
    },
    { name: "thumbnailUrl", label: "Thumbnail", type: "upload", accept: "image/*" },
    {
      name: "provider",
      label: "Provider",
      type: "select",
      options: [{ value: "", label: "Work it out from the link" }, ...PROVIDER_OPTIONS],
      help: "Named platforms play in the page; “Other site” shows a link out.",
    },
    { name: "durationSeconds", label: "Duration (seconds)", type: "number" },
    { name: "publishedAt", label: "Published at", type: "datetime" },
    { name: "tags", label: "Tags", type: "tags", help: "Comma-separated, e.g. surf, bali" },
    ...accessFields(members),
    {
      name: "featured",
      label: "On the front page",
      type: "checkbox",
      help: "The home page rotates through the featured videos. Order them on the Front page tab.",
    },
  ];
}

function toForm(v?: Video): FormValues {
  return {
    title: v?.title ?? "",
    description: v?.description ?? "",
    url: v?.url ?? "",
    thumbnailUrl: v?.thumbnailUrl ?? "",
    provider: v?.provider ?? "",
    durationSeconds: v?.durationSeconds ?? null,
    publishedAt: isoToLocalInput(v?.publishedAt),
    collection: v?.collection ?? "life",
    tags: tagsToInput(v?.tags),
    visibility: v?.visibility ?? "PUBLIC",
    sharedWith: v?.sharedWith ?? [],
    featured: v?.featured ?? false,
  };
}

function toRequest(v: FormValues): VideoRequest {
  return {
    title: String(v.title ?? "").trim(),
    description: toNullableString(v.description),
    url: String(v.url ?? "").trim(),
    thumbnailUrl: toNullableString(v.thumbnailUrl),
    provider: (toNullableString(v.provider) as VideoProvider | null) ?? null,
    durationSeconds: toNullableNumber(v.durationSeconds),
    publishedAt: localInputToIso(v.publishedAt as string),
    collection: toNullableString(v.collection),
    tags: parseTags(v.tags),
    visibility: (v.visibility as Visibility) || "PUBLIC",
    sharedWith: toStringList(v.sharedWith),
    featured: Boolean(v.featured),
  };
}

/** Uploading a video file also captures a poster frame, and marks it as a hosted file. */
async function upload(file: File, field: FieldDef) {
  if (field.name !== "url") return (await adminApi.uploadFile(file)).url;
  const up = await uploadVideoFile(file);
  return {
    url: up.url,
    fill: { thumbnailUrl: up.thumbnailUrl ?? "", provider: "FILE" },
    fillIfEmpty: { title: titleFromFilename(file.name) },
  };
}

export function AdminVideos() {
  const { refreshToken } = useOutletContext<AdminOutletContext>();
  const [reload, setReload] = useState(0);
  const { data, loading, error } = useAsync(() => adminApi.listVideos(), [refreshToken, reload]);
  const { collections } = useCollections();
  const members = useMembers(refreshToken);
  const FIELDS = fields(collections, members);
  const rows = data?.items ?? [];
  const [editing, setEditing] = useState<Video | null>(null);
  const [creating, setCreating] = useState(false);
  const [importing, setImporting] = useState(false);
  const [dialog, setDialog] = useState<OpenBulkDialog | null>(null);
  const refetch = () => setReload((n) => n + 1);

  async function apply(change: Omit<BulkUpdateRequest, "ids">, ids: number[]) {
    await adminApi.bulkVideos({ ids, ...change });
    refetch();
  }

  const columns: Column<Video>[] = [
    { key: "thumb", header: "", render: (v) => <Thumb url={v.thumbnailUrl} /> },
    {
      key: "code",
      header: "Code",
      render: (v) => <span className="mono">{accession("VIDEO", v.id)}</span>,
      sortValue: (v) => v.id,
    },
    {
      key: "title",
      header: "Title",
      className: "cell-title",
      render: (v) => v.title,
      sortValue: (v) => v.title,
    },
    {
      key: "provider",
      header: "Provider",
      render: (v) => <span className="pill">{PROVIDER_LABELS[v.provider] ?? v.provider}</span>,
      sortValue: (v) => v.provider,
    },
    {
      key: "visibility",
      header: "Visible to",
      render: (v) => <VisibilityCell row={v} />,
      sortValue: (v) => v.visibility,
    },
    {
      key: "featured",
      header: "Front page",
      render: (v) => (
        <span className="cell-muted">{v.featured ? (v.featuredRank ? `#${v.featuredRank}` : "yes") : "—"}</span>
      ),
      sortValue: (v) => (v.featured ? (v.featuredRank ?? 9999) : 99999),
    },
    {
      key: "duration",
      header: "Length",
      render: (v) => <span className="mono">{formatDuration(v.durationSeconds) || "—"}</span>,
      sortValue: (v) => v.durationSeconds ?? 0,
    },
    {
      key: "publishedAt",
      header: "Published",
      render: (v) => <span className="mono">{formatDate(v.publishedAt) || "—"}</span>,
      sortValue: (v) => v.publishedAt ?? "",
    },
  ];

  return (
    <>
      <AdminTable
        rows={rows}
        getId={(v) => v.id}
        columns={columns}
        searchText={(v) => `${v.title} ${v.tags.join(" ")} ${v.description ?? ""} ${v.sharedWith.join(" ")}`}
        searchPlaceholder="Filter videos…"
        typeFilter={{
          label: "Provider",
          value: (v) => v.provider,
          options: PROVIDER_OPTIONS,
        }}
        filters={mediaFilters<Video>(collections)}
        loading={loading}
        error={error ?? undefined}
        emptyLabel="No videos yet."
        createLabel="+ New video"
        toolbarExtra={
          <button className="btn btn--ghost" onClick={() => setImporting(true)}>
            ⇪ Add videos…
          </button>
        }
        viewHref={(v) => `/documents/VIDEO-${v.id}`}
        onCreate={() => setCreating(true)}
        onEdit={(v) => setEditing(v)}
        onDelete={async (v) => {
          await adminApi.deleteVideo(v.id);
          refetch();
        }}
        bulkActions={mediaBulkActions<Video>({
          noun: "videos",
          apply,
          openDialog: setDialog,
          remove: async (sel) => {
            for (const v of sel) await adminApi.deleteVideo(v.id);
            refetch();
          },
        })}
      />
      {creating && (
        <AdminForm
          title="New video"
          fields={FIELDS}
          initial={toForm()}
          submitLabel="Create"
          onUpload={upload}
          onCancel={() => setCreating(false)}
          onSubmit={async (v) => {
            await adminApi.createVideo(toRequest(v));
            setCreating(false);
            refetch();
          }}
        />
      )}
      {editing && (
        <AdminForm
          title={`Edit ${accession("VIDEO", editing.id)}`}
          fields={FIELDS}
          initial={toForm(editing)}
          onUpload={upload}
          onCancel={() => setEditing(null)}
          onSubmit={async (v) => {
            await adminApi.updateVideo(editing.id, toRequest(v));
            setEditing(null);
            refetch();
          }}
        />
      )}
      {importing && (
        <MediaImport
          kind="video"
          collections={collections}
          members={members}
          onClose={(changed) => {
            setImporting(false);
            if (changed) refetch();
          }}
        />
      )}
      {dialog && (
        <BulkDialog
          dialog={dialog}
          noun="videos"
          members={members}
          collections={collections}
          onCancel={() => setDialog(null)}
          onApply={async (change) => {
            await apply(change, dialog.ids);
            setDialog(null);
          }}
        />
      )}
    </>
  );
}
